import React, { useState, useEffect, useRef } from 'react';
import { Search, Calendar, User, Clock, Loader2 } from 'lucide-react';
import { db } from '../Firebase';
import { collection, onSnapshot } from 'firebase/firestore';

export default function Blogs() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const scrollRef = useRef(null);
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    setIsDown(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDown(false);
  };

  const handleMouseUp = () => {
    setIsDown(false);
  };

  const handleMouseMove = (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // scroll multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const categories = ['All', 'Hospital Setup', 'OT Planning', 'ICU Planning', 'Medical Equipment', 'Healthcare Industry', 'Pharmaceutical Distribution'];

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const blogsRef = collection(db, 'blogs');

    const unsubscribe = onSnapshot(blogsRef, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const unique = [];
      const seen = new Set();
      for (const b of list) {
        if (b.title) {
          const norm = b.title.trim().toLowerCase();
          if (!seen.has(norm)) {
            seen.add(norm);
            unique.push(b);
          }
        }
      }
      setBlogs(unique);
      setLoading(false);
    }, (err) => {
      console.error("Error loading blogs:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const filteredPosts = blogs.filter(p => {
    const matchesSearch = p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.desc?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="font-sans bg-brand-light min-h-screen">

      {/* Page Header */}
      <section className="bg-brand-navy text-white pt-28 pb-16 relative overflow-hidden grid-mesh">
        <div className="absolute inset-0 bg-brand-navy/85 z-10" />
        <div className="absolute inset-0 z-0 bg-brand-navy/40" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20 text-center space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-green font-poppins block">Knowledge Desk</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-montserrat text-white tracking-tight">
            Healthcare Planning & Insights
          </h1>
          <p className="text-sm text-brand-silver max-w-2xl mx-auto font-poppins">
            Professional guidelines, compliance updates, and modular design strategies penned by technical planners.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-12 max-w-7xl mx-auto px-6 lg:px-8">

        {/* Top Filters & Search Area */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 bg-white p-5 rounded-2xl border border-brand-silver/50 shadow-sm select-none">
          {/* Horizontal Scrollable Categories */}
          <div
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            className={`flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 flex-grow max-w-full md:max-w-[70%] scrollbar-none ${isDown ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 text-xs font-bold font-poppins rounded-xl transition-colors border whitespace-nowrap shrink-0 ${selectedCategory === cat
                  ? 'bg-brand-blue border-brand-blue text-white shadow-md shadow-brand-blue/10'
                  : 'bg-brand-light border-brand-silver/50 text-gray-600 hover:bg-brand-silver/40'
                  }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Search Input Widget */}
          <div className="relative w-full md:w-72 shrink-0">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue font-poppins text-xs"
            />
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
          </div>
        </div>

        {/* Blogs Grid Area (2 columns per row) */}
        {loading ? (
          <div className="bg-white p-12 text-center rounded-2xl border border-brand-silver/30 shadow-sm flex flex-col items-center justify-center space-y-4">
            <Loader2 className="animate-spin text-brand-blue" size={40} />
            <p className="text-xs font-bold text-gray-500 font-poppins">Loading published insights...</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="bg-white p-12 text-center rounded-2xl border border-brand-silver/30 shadow-sm">
            <h3 className="text-lg font-bold text-brand-navy font-montserrat">No articles match your selection</h3>
            <p className="text-xs text-gray-500 font-poppins mt-1">Try resetting the search or category filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredPosts.map((post, idx) => (
              <article key={idx} className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/30 shadow-sm space-y-4 hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                <div className="space-y-4">
                  {post.image && (
                    <div className="w-full h-48 sm:h-56 rounded-2xl overflow-hidden relative shadow-sm border border-brand-silver/40">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                    </div>
                  )}
                  <div className="flex flex-wrap items-center justify-between text-xs font-bold font-poppins text-brand-blue uppercase tracking-wider pb-3 border-b border-brand-silver">
                    <span className="bg-brand-blue/10 px-2.5 py-0.5 rounded">{post.category}</span>
                  </div>

                  <h2 className="text-xl font-extrabold font-montserrat text-brand-navy hover:text-brand-blue transition-colors cursor-pointer line-clamp-2">
                    {post.title}
                  </h2>

                  <div className="flex flex-wrap items-center gap-4 text-[12px] text-gray-400 font-poppins pt-1">
                    <span className="flex items-center gap-1.5"><Calendar size={14} className="text-brand-blue" /> {post.date}</span>
                    <span className="flex items-center gap-1.5"><User size={14} className="text-brand-blue" /> {post.author}</span>
                    <span className="flex items-center gap-1.5"><Clock size={14} className="text-brand-blue" /> {post.readTime}</span>
                  </div>

                  <p className="text-[13px] text-gray-500 font-poppins leading-relaxed line-clamp-3">
                    {post.desc}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

    </div>
  );
}
